import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { normalizeSrc, resolveImage, onImgError } from "@/lib/images";
import { useParams, Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchTemple } from "@/lib/api";
import RichText from "@/components/RichText";
import Seo from "@/components/Seo";
import { placeLd, breadcrumbLd, graph } from "@/lib/jsonld";
import PageEngage from "@/components/PageEngage";
import { ChevronRight, MapPin, Globe, CalendarCheck, Clock, Facebook, Instagram, Youtube, Twitter } from "lucide-react";
import OmSpinner from "@/components/OmSpinner";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, youtube: Youtube, x: Twitter };

export default function TempleDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const { pick } = useLang();
  const [t, setT] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setT(null); setNotFound(false);
    fetchTemple(slug).then(setT).catch(() => setNotFound(true));
  }, [slug, location.pathname]);

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3"><T>Temple not found.</T></div>;
  if (!t) return <OmSpinner />;

  const meta = [
    [pick("देवता", "Deity"), t.deity],
    [pick("स्थान", "Location"), `${t.location}, ${t.state}`],
    [pick("क्षेत्र", "Region"), t.region],
    [pick("काल", "Era"), t.era],
  ].filter(([, v]) => v);

  return (
    <article className="relative pb-24" data-testid="temple-detail">
      <Seo
        path={`/temples/${t.slug}`}
        title={t.name}
        description={t.description || t.significance}
        image={resolveImage(t)}
        type="article"
        jsonLd={graph(
          placeLd({ name: t.name, description: t.description || t.significance, image: resolveImage(t), path: `/temples/${t.slug}`, location: t.location, state: t.state, deity: t.deity }),
          breadcrumbLd([{ name: "Dharmartha", path: "/" }, { name: "Temples", path: "/temples" }, { name: t.name, path: `/temples/${t.slug}` }])
        )}
      />
      <div className="relative h-[42vh] min-h-[300px] overflow-hidden border-b border-line bg-[var(--parchment)]">
        <img src={resolveImage(t)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-45 blur-2xl" />
        <img src={resolveImage(t)} alt={t.name} onError={onImgError(t)} className="absolute inset-0 mx-auto h-full w-auto max-w-full object-contain" />
      </div>

      <div className="mx-auto max-w-4xl px-5 md:px-8">
        <header className="pt-7 md:pt-8" data-testid="temple-header">
          <p className="font-deva text-2xl text-saffron deva-lh">{t.deva_name}</p>
          <h1 className="mt-1 font-serif-latin text-4xl text-ink md:text-5xl">{t.name}</h1>
          <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-ink-3"><MapPin className="h-4 w-4" /> {t.location}, {t.state}</p>
        </header>
        <nav className="flex items-center gap-1.5 py-6 text-xs text-ink-3">
          <Link to="/" className="hover:text-saffron"><T>Dharmartha</T></Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/temples" className="hover:text-saffron">{pick("मंदिर", "Temples")}</Link>
        </nav>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-5 border-y border-line py-6 sm:grid-cols-4">
          {meta.map(([label, value]) => (
            <div key={label}>
              <dt className="overline-label text-ink-3">{label}</dt>
              <dd className="mt-1.5 font-serif-latin text-[15px] leading-snug text-ink">{value}</dd>
            </div>
          ))}
        </dl>

        <RichText value={pick(t.description_hi, t.description)} className="mt-10 max-w-[65ch] font-serif-latin text-lg leading-relaxed text-ink-2" />

        {t.deity_refs?.length > 0 && (
          <section className="mt-10" data-testid="temple-deities">
            <p className="overline-label mb-4 text-ink-3">{pick("प्रमुख देवता", "Presiding Deities")}</p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {t.deity_refs.map((d) => (
                <Link key={d.slug} to={`/deities/${d.slug}`} data-testid={`temple-deity-${d.slug}`}
                  className="group flex flex-col items-center overflow-hidden rounded-xl border border-line bg-[var(--surface)] p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-lg">
                  <div className="h-20 w-20 overflow-hidden rounded-full border border-line bg-[var(--parchment)]">
                    {d.image
                      ? <img src={d.image} alt={d.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      : <span className="flex h-full w-full items-center justify-center font-deva text-2xl text-saffron/70">{(d.deva_name || d.name || "")[0]}</span>}
                  </div>
                  <p className="mt-3 font-deva text-base text-ink deva-lh">{d.deva_name}</p>
                  <p className="font-serif-latin text-sm text-ink-2">{d.name}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {(t.website || t.booking_url) && (
          <div className="mt-8 flex flex-wrap gap-3">
            {t.booking_url && (
              <a href={t.booking_url} target="_blank" rel="noopener noreferrer" data-testid="temple-booking-btn"
                className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                <CalendarCheck className="h-4 w-4" /> {pick("पूजा / सेवा बुकिंग", "Pooja / Seva Booking")}
              </a>
            )}
            {t.website && (
              <a href={t.website} target="_blank" rel="noopener noreferrer" data-testid="temple-website-btn"
                className="inline-flex items-center gap-2 border border-line px-6 py-3 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
                <Globe className="h-4 w-4" /> {pick("आधिकारिक वेबसाइट", "Official Website")}
              </a>
            )}
          </div>
        )}

        {t.timings && (
          <div className="mt-10 flex items-start gap-3 border border-line bg-[var(--surface-alt)]/40 px-6 py-5" data-testid="temple-timings">
            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
            <div>
              <p className="overline-label text-ink-3">{pick("दर्शन समय", "Darshan Timings")}</p>
              <p className="mt-1.5 font-serif-latin text-[16px] text-ink">{t.timings}</p>
            </div>
          </div>
        )}

        {(t.aarti_timings || t.darshan_info || t.pooja_info) && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="temple-seva-details">
            {t.aarti_timings && (
              <div className="border border-line bg-[var(--surface)] px-5 py-4" data-testid="temple-aarti">
                <p className="overline-label text-saffron">{pick("आरती समय", "Aarti Timings")}</p>
                <p className="mt-1.5 whitespace-pre-line font-serif-latin text-[15px] leading-relaxed text-ink-2">{t.aarti_timings}</p>
              </div>
            )}
            {t.darshan_info && (
              <div className="border border-line bg-[var(--surface)] px-5 py-4" data-testid="temple-darshan">
                <p className="overline-label text-saffron">{pick("दर्शन जानकारी", "Darshan Info")}</p>
                <p className="mt-1.5 whitespace-pre-line font-serif-latin text-[15px] leading-relaxed text-ink-2">{t.darshan_info}</p>
              </div>
            )}
            {t.pooja_info && (
              <div className="border border-line bg-[var(--surface)] px-5 py-4 sm:col-span-2" data-testid="temple-pooja">
                <p className="overline-label text-saffron">{pick("पूजा / सेवा विवरण", "Pooja / Seva Details")}</p>
                <div className="mt-1.5 max-w-[65ch] text-[15px] leading-relaxed text-ink-2"><RichText value={pick(t.pooja_info_hi, t.pooja_info) || t.pooja_info} /></div>
              </div>
            )}
          </div>
        )}

        {t.festivals?.length > 0 && (
          <section className="mt-10">
            <p className="overline-label mb-4 text-ink-3">{pick("प्रमुख पर्व", "Major Festivals")}</p>
            <div className="flex flex-wrap gap-2.5">
              {t.festivals.map((f, i) => (
                <span key={i} className="rounded-full border border-line bg-[var(--surface)] px-4 py-1.5 text-sm text-ink-2">{f}</span>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          <div className="mb-5 flex items-baseline gap-3">
            <span className="h-px w-8 bg-[var(--gold)]" />
            <h3 className="font-deva text-2xl text-saffron deva-lh">महत्त्व</h3>
            <span className="overline-label text-ink-3"><T>Significance</T></span>
          </div>
          <RichText value={pick(t.significance_hi, t.significance)} className="max-w-[65ch] font-serif-latin text-[17px] leading-[1.9] text-ink-2" />
        </section>

        {t.highlights?.length > 0 && (
          <section className="mt-12">
            <p className="overline-label mb-4 text-ink-3">{pick("विशेषताएँ", "Highlights")}</p>
            <ul className="grid gap-3 sm:grid-cols-2">
              {t.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[16px] text-ink-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-[var(--gold)]" />{h}
                </li>
              ))}
            </ul>
          </section>
        )}

        {t.social && Object.keys(t.social).length > 0 && (
          <section className="mt-10">
            <p className="overline-label mb-4 text-ink-3">{pick("सोशल मीडिया", "Follow the temple")}</p>
            <div className="flex flex-wrap gap-3">
              {Object.entries(t.social).map(([k, url]) => {
                const Icon = SOCIAL_ICONS[k];
                return Icon ? (
                  <a key={k} href={url} target="_blank" rel="noopener noreferrer" data-testid={`temple-social-${k}`} aria-label={k}
                    className="flex h-10 w-10 items-center justify-center border border-line text-ink-2 transition-colors hover:border-[var(--saffron)] hover:text-saffron">
                    <Icon className="h-4 w-4" />
                  </a>
                ) : null;
              })}
            </div>
          </section>
        )}

        <Link to="/temples" className="mt-14 inline-flex items-center gap-2 text-sm text-saffron hover:translate-x-0.5 transition-transform">
          ← {pick("सभी मंदिर देखें", "Explore all temples")}
        </Link>
      </div>
      <PageEngage refType="temple" refSlug={slug} refTitle={t.name} />
    </article>
  );
}
